import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserCog, Plus, Pencil, Trash2, Loader2, Check, X, Wand2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function ResponsiblesManager() {
  const { committee, user } = useAuth();
  const navigate = useNavigate();
  const [committees, setCommittees] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [delTarget, setDelTarget] = useState(null);
  const [normPlan, setNormPlan] = useState(null);
  const [normBusy, setNormBusy] = useState(false);

  const previewNormalize = async () => {
    setNormBusy(true);
    try {
      const { data } = await api.post("/responsibles/normalize-names");
      if (!data.voter_changes.length && !data.responsible_changes.length) {
        toast.success("Tüm isimler zaten tek biçimde");
        setNormPlan(null);
      } else setNormPlan(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setNormBusy(false); }
  };

  const applyNormalize = async () => {
    setNormBusy(true);
    try {
      const { data } = await api.post("/responsibles/normalize-names?apply=true");
      toast.success(`${data.updated_firms} firma güncellendi · ${data.merged_responsibles} sorumlu kaydı birleştirildi`);
      setNormPlan(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setNormBusy(false); }
  };

  const cno = committee === "all" ? null : Number(committee);

  useEffect(() => { api.get("/committees").then(({ data }) => setCommittees(data)); }, []);

  const load = useCallback(() => {
    if (cno == null) { setItems([]); setLoading(false); return; }
    setLoading(true);
    api.get("/responsibles", { params: { committee_no: cno } })
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [cno]);
  useEffect(() => { load(); }, [load]);

  const committeeName = committees.find((c) => c.committee_no === cno)?.name || "";

  const add = async () => {
    const name = newName.trim();
    if (!name || cno == null) return;
    setAdding(true);
    try {
      await api.post("/responsibles", { name, committee_no: cno });
      toast.success(`${name} eklendi`);
      setNewName("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAdding(false); }
  };

  const saveEdit = async (id) => {
    const name = editName.trim();
    if (!name) return;
    try {
      const { data } = await api.patch(`/responsibles/${id}`, { name });
      toast.success(`Güncellendi${data.updated_firms ? ` · ${data.updated_firms} firma güncellendi` : ""}`);
      setEditId(null); setEditName("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async () => {
    if (!delTarget) return;
    try {
      const { data } = await api.delete(`/responsibles/${delTarget.id}`);
      toast.success(`${delTarget.name} silindi${data.unassigned ? ` · ${data.unassigned} firmanın sorumlusu kaldırıldı` : ""}`);
      setDelTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="responsibles-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-foreground flex items-center gap-2">
          <UserCog className="h-7 w-7 text-cyan-600" /> Sorumlu Yönetimi
        </h1>
        <p className="text-sm text-muted-foreground">
          {cno != null ? <>#{cno} {committeeName} komitesi için sorumlular</> : "Yönetmek için üstten bir komite seçin"}
        </p>
      </div>

      {user?.role === "super_admin" && (
        <Card className="p-4 space-y-3" data-testid="normalize-names-card">
          <div className="flex items-center gap-2 flex-wrap">
            <Wand2 className="h-4 w-4 text-cyan-600" />
            <p className="text-sm font-medium">İsimleri Birleştir</p>
            <span className="text-xs text-muted-foreground">Farklı yazılmış sorumlu adlarını "Ad Soyad" biçiminde tek isme indirir</span>
            <Button variant="outline" size="sm" className="ml-auto" disabled={normBusy} onClick={previewNormalize} data-testid="normalize-preview-btn">
              {normBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Önizle"}
            </Button>
          </div>
          {normPlan && (
            <div className="space-y-2" data-testid="normalize-plan">
              <p className="text-xs text-muted-foreground">
                <b className="text-foreground">{normPlan.total_firms}</b> firma, <b className="text-foreground">{normPlan.responsible_changes.length}</b> sorumlu kaydı etkilenecek:
              </p>
              <div className="max-h-56 overflow-y-auto rounded-md border border-border divide-y divide-border/60">
                {normPlan.voter_changes.map((p) => (
                  <div key={p.from} className="px-3 py-1.5 text-xs flex items-center gap-2" data-testid={`normalize-row-${p.from}`}>
                    <span className="text-muted-foreground line-through">{p.from}</span>
                    <span>→</span>
                    <span className="font-medium">{p.to}</span>
                    <span className="ml-auto text-muted-foreground">{p.firms} firma</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setNormPlan(null)} data-testid="normalize-cancel">Vazgeç</Button>
                <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" disabled={normBusy} onClick={applyNormalize} data-testid="normalize-apply-btn">Uygula</Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {cno == null ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="responsibles-no-committee">
          Sorumluları yönetmek için üstteki menüden belirli bir komite seçin ("Genel Durum" değil).
        </Card>
      ) : (
        <>
          <Card className="p-4">
            <div className="flex gap-2">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && add()}
                placeholder="Yeni sorumlu adı..."
                data-testid="new-responsible-input"
              />
              <Button className="bg-[#0F2C59] hover:bg-[#0F2C59]/90" disabled={!newName.trim() || adding} onClick={add} data-testid="add-responsible-button">
                {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" />Ekle</>}
              </Button>
            </div>
          </Card>

          <Card className="divide-y divide-border" data-testid="responsibles-list">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">Yükleniyor...</div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">Bu komiteye atanmış sorumlu yok. Yukarıdan ekleyin.</div>
            ) : items.map((r) => (
              <div key={r.id} className="flex items-center gap-3 p-3" data-testid={`responsible-row-${r.id}`}>
                <div className="h-9 w-9 rounded-full bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 grid place-items-center text-xs font-bold">
                  {(r.name || "?").slice(0, 2).toUpperCase()}
                </div>
                {editId === r.id ? (
                  <>
                    <Input value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveEdit(r.id)} className="flex-1 h-9" data-testid={`edit-responsible-input-${r.id}`} autoFocus />
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600" onClick={() => saveEdit(r.id)} data-testid={`save-responsible-${r.id}`}><Check className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditId(null); setEditName(""); }}><X className="h-4 w-4" /></Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 font-medium" data-testid={`responsible-name-${r.id}`}>{r.name}</span>
                    <button type="button" onClick={() => navigate(`/secmenler?caller=${encodeURIComponent(r.name)}`)}
                      className="text-xs font-medium px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 whitespace-nowrap hover:bg-cyan-500/25 transition-colors cursor-pointer"
                      data-testid={`responsible-count-${r.id}`} title={`${r.name} sorumlusunun firmalarını göster`}>
                      {r.assigned_count ?? 0} firma →
                    </button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditId(r.id); setEditName(r.name); }} data-testid={`edit-responsible-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => setDelTarget(r)} data-testid={`delete-responsible-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </>
                )}
              </div>
            ))}
          </Card>
          <p className="text-xs text-muted-foreground">Bir sorumluyu yeniden adlandırdığınızda o komitedeki firmaların sorumlusu da güncellenir. Sildiğinizde firmaların sorumlusu boşa çıkar.</p>
        </>
      )}

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent data-testid="delete-responsible-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{delTarget?.name} silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>Bu komitede bu sorumluya atanmış firmaların sorumlusu boşaltılacak.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive hover:bg-destructive/90" onClick={del} data-testid="confirm-delete-responsible">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
